import { Provider } from 'react'
import { Document, BLOCKS, INLINES, Inline, Block } from '@contentful/rich-text-types'
import { documentToReactComponents, Options } from '@contentful/rich-text-react-renderer'
import { documentToHtmlString, Options as HtmlOptions } from '@contentful/rich-text-html-renderer'
import { BrowserName, DesktopOperatingSystemName } from '../../helpers/device'
import { InlineLink } from './inline-link'
import { InlineEmbeddedEntry, inlineEmbeddedEntryHtmlString } from './inline-embedded-entry'
import { UlList } from './ul-list'
import { OlList } from './ol-list'
import { Table, TableCell } from './table'
import { ListItem } from './list-item'

export const renderRichTextSingleLineContent = (richText: Document) => {
  return renderRichText(richText, {
    [BLOCKS.PARAGRAPH]: (_, children) => <>{children}</>,
  })
}

export const renderRichTextMultiLineContent = (richText: Document) => {
  return renderRichText(richText, {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <>
        {children}
        <br />
      </>
    ),
  })
}

const renderRichText = (richText: Document, renderNodeOptions?: Options['renderNode']) => {
  return documentToReactComponents(richText, {
    renderNode: {
      ...renderNodeOptions,
      [INLINES.HYPERLINK]: (node, children) => <InlineLink {...(node as Inline)}>{children}</InlineLink>,
      [INLINES.EMBEDDED_ENTRY]: (node) => <InlineEmbeddedEntry {...(node as Inline)} />,
      [BLOCKS.UL_LIST]: (node, children) => <UlList {...(node as Block)}>{children}</UlList>,
      [BLOCKS.OL_LIST]: (node, children) => <OlList {...(node as Block)}>{children}</OlList>,
      [BLOCKS.LIST_ITEM]: (node, children) => <ListItem {...(node as Block)}>{children}</ListItem>,
      [BLOCKS.TABLE]: (node, children) => <Table {...(node as Block)}>{children}</Table>,
      [BLOCKS.TABLE_CELL]: (node, children) => <TableCell {...(node as Block)}>{children}</TableCell>,
    },
    renderText: (text) => {
      let parts: (string | JSX.Element)[] = text.split(/[{}]/)
      if (parts.length > 1) {
        parts = parts.map((part) => {
          if (part === 'browserName') {
            return <BrowserName key={part} />
          } else if (part === 'operatingSystemName') {
            return <DesktopOperatingSystemName key={part} />
          }
          return part
        })
      }
      return <>{parts}</>
    },
  })
}

/**
 *
 * @param richText
 * @param renderNodeOptions
 * @param ContextProvider since this function uses React's `renderToString()` function which loses any Context around it, we need to pass the Context Provider of the Context we want to preserve so that we can re-create it within the `renderToString()` call
 * @param contextValue  the corresponding value of the Context we want to preserve
 */
export const renderRichTextToHtmlString = <C extends object | undefined>(
  richText: Document,
  renderNodeOptions?: HtmlOptions['renderNode'],
  ContextProvider?: Provider<C>,
  contextValue?: C
): string => {
  return documentToHtmlString(richText, {
    renderNode: {
      ...renderNodeOptions,
      [INLINES.EMBEDDED_ENTRY]: (node) => inlineEmbeddedEntryHtmlString(node as Inline, ContextProvider, contextValue),
    },
  })
}
